// src/pages/Base/components/SessionInfo/SessionInfo.tsx

import React, { useEffect, useState, useRef } from 'react';
import ReactDOM from 'react-dom';
import './SessionInfo.scss';
import {
  HiOutlinePlay,
  HiOutlineClock,
  HiOutlineChevronDown,
  HiOutlineChevronUp,
  HiOutlineUserGroup,
  HiOutlinePlus,
} from 'react-icons/hi2';
import { toast } from 'react-toastify';
import StartSession from './components/StartSession/StartSession';
import EndSession from './components/EndSession/EndSession';
import SessionAlert from './components/SessionAlert/SessionAlert';
import NewShiftModal from './components/NewShiftModal/NewShiftModal';
import moment from 'moment';
import { formatDateTime } from '../../../../../../utils/functions';
import { LocalSession } from '../../../../../../types/LocalSession';
import { getActiveLocalSessionByLocalId } from '../../../../../../crud/management_api/local_sessions';

interface SessionInfoProps {
  session: LocalSession | null;
  setSession: (session: LocalSession | null) => void;
}

const SessionInfo: React.FC<SessionInfoProps> = ({ session, setSession }) => {
  const [showStartSession, setShowStartSession] = useState(false);
  const [showEndSession, setShowEndSession] = useState(false);
  const [showNewShift, setShowNewShift] = useState(false);
  const [elapsedTime, setElapsedTime] = useState<string>('');
  const [isExpanded, setIsExpanded] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);


  // Detect mobile screen size
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Update the elapsed time every second when the session is active
  useEffect(() => {
    let timer: NodeJS.Timeout | null = null;
    if (session && session.active) {
      timer = setInterval(() => {
        const startTime = moment(session.session_start);
        const now = moment();
        const duration = moment.duration(now.diff(startTime));
        const hours = Math.floor(duration.asHours());
        const minutes = Math.floor(duration.minutes());
        setElapsedTime(`${hours}h ${minutes}m`);
      }, 1000);
    } else {
      setElapsedTime('');
    }
    return () => {
      if (timer) clearInterval(timer);
    };
  }, [session]);

  // Close panel when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      // Don't close if any modal is open
      if (showStartSession || showEndSession || showNewShift) {
        return;
      }
      
      // On mobile, check if click is on the panel itself (rendered as portal)
      const target = event.target as HTMLElement;
      if (isMobile) {
        // Check if the click is inside the session-details-panel
        const panel = document.querySelector('.session-details-panel');
        if (panel && panel.contains(target)) {
          return; // Don't close if clicking inside the panel
        }
      } else {
        // Desktop: use panelRef
        if (panelRef.current && panelRef.current.contains(target)) {
          return;
        }
      }
      
      setIsExpanded(false);
    };

    if (isExpanded) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isExpanded, showStartSession, showEndSession, showNewShift, isMobile]);

  const handleShiftCreated = async () => {
    // Refresh session data after creating a new shift
    try {
      // Add a small delay to ensure backend has processed the shift creation
      // and cache has been cleared
      await new Promise(resolve => setTimeout(resolve, 500));
      
      const user = localStorage.getItem('user');
      if (user) {
        const userObj = JSON.parse(user);
        
        // Force a cache-bypassed request by adding a timestamp query parameter
        const response = await getActiveLocalSessionByLocalId(userObj.local_id);
        
        if (response.data) {
          setSession(response.data);
          
          // Verify that shifts were actually updated
          if (response.data.shifts && response.data.shifts.length > 0) {
            const hasActiveShift = response.data.shifts.some((s: any) => s.active);
            if (!hasActiveShift) {
              console.warn('No active shift found after creation, might need page refresh');
              toast.warning('Sesión actualizada. Si no ves el nuevo turno, recarga la página.');
            }
          }
        } else {
          console.warn('No session data returned after shift creation');
          toast.warning('No se pudo actualizar la sesión. Por favor, recarga la página.');
        }
      }
    } catch (error) {
      console.error('Error refreshing session:', error);
      // If refresh fails, show a toast to inform the user to refresh manually
      toast.error('Error al actualizar la sesión. Por favor, recarga la página.');
    }
  };

  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };

  // Handle opening new shift modal
  const handleOpenNewShift = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setShowNewShift(true);
    // Don't close panel immediately on mobile - let the modal render first
  };

  // Handle opening end session modal
  const handleOpenEndSession = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsExpanded(false);
    setShowEndSession(true);
  };

  // Handle closing new shift modal
  const handleCloseNewShift = () => {
    setShowNewShift(false);
    if (isMobile) {
      setIsExpanded(false); // Close panel on mobile after modal closes
    }
  };

  // Handle shift created
  const handleShiftCreatedWrapper = async () => {
    await handleShiftCreated();
    if (isMobile) {
      setIsExpanded(false); // Close panel on mobile after shift is created
    }
  };

  // ――― Render when no session exists
  const renderNoSession = () => (
    <div className="session-compact-container">
      <button 
        className={`session-compact-button no-session-btn ${isMobile ? 'mobile-play-only' : ''}`} 
        onClick={() => setShowStartSession(true)}
      >
        <HiOutlinePlay className="compact-icon" />
        {!isMobile && <span className="compact-text">Iniciar Sesión</span>}
      </button>
    </div>
  );

  // ――― Render when session exists but is not active
  const renderLastSession = () => {
    if (!session) return null;

    const startTime = moment(session.session_start);
    const endTime = moment(session.session_end);
    const duration = moment.duration(endTime.diff(startTime));
    const hours = Math.floor(duration.asHours());
    const minutes = Math.floor(duration.minutes());

    return (
      <>
        {/* On desktop: show last session info + start button */}
        {!isMobile && (
          <>
            <div className="session-compact-container">
              <button className="session-compact-button inactive-session-btn" onClick={() => setShowStartSession(true)}>
                <span className="session-label">ÚLTIMA SESIÓN:</span>
                <span className="session-value">{hours}h {minutes}m</span>
                <HiOutlineClock className="compact-icon" />
              </button>
            </div>
            
            <div className="session-compact-container" style={{ marginLeft: '0.5rem' }}>
              <button className="session-compact-button no-session-btn" onClick={() => setShowStartSession(true)}>
                <HiOutlinePlay className="compact-icon" />
                <span className="compact-text">Iniciar Sesión</span>
              </button>
            </div>
          </>
        )}
        
        {/* On mobile: only show play button icon (no text, no last session) */}
        {isMobile && (
          <div className="session-compact-container">
            <button className="session-compact-button no-session-btn mobile-play-only" onClick={() => setShowStartSession(true)}>
              <HiOutlinePlay className="compact-icon" />
            </button>
          </div>
        )}
      </>
    );
  };

  // ――― Render when session is active
  const renderActiveSession = () => {
    if (!session) return null;

    const activeShift = session.shifts?.find(s => s.active);

    // Panel content to be rendered
    const panelContent = (
      <div className="session-details-panel">
        {/* Session Header */}
        <div className="panel-header">
          <div className="panel-title">
            <HiOutlineClock className="title-icon" />
            <span>Sesión Activa</span>
          </div>
          <button className="close-panel-btn" onClick={() => setIsExpanded(false)}>
            <HiOutlineChevronUp size={14} />
          </button>
        </div>

        {/* Session Info */}
        <div className="panel-section session-overview">
          <div className="info-row">
            <span className="info-label">Iniciado por:</span>
            <span className="info-value">{session.username}</span>
          </div>
          <div className="info-row">
            <span className="info-label">Hora de inicio:</span>
            <span className="info-value">{formatDateTime(session.session_start)}</span>
          </div>
          <div className="info-row">
            <span className="info-label">Duración total:</span>
            <span className="info-value duration-badge">{elapsedTime}</span>
          </div>
        </div>

        {/* Shifts Section */}
        <div className="panel-section shifts-section">
          <div className="section-header">
            <div className="section-title-group">
              <HiOutlineUserGroup className="section-icon" />
              <span className="section-title">Turnos</span>
            </div>
            {activeShift && (
              <button
                className="new-shift-btn"
                onClick={handleOpenNewShift}
              >
                <HiOutlinePlus size={13} />
                Nuevo Turno
              </button>
            )}
          </div>

          <div className="shifts-list">
            {session.shifts && session.shifts.length > 0 ? (
              session.shifts.map((shift) => (
                <div 
                  key={shift.shift_id} 
                  className={`shift-card ${shift.active ? 'active-shift' : 'closed-shift'}`}
                >
                  <div className="shift-header">
                    <div className="shift-name">
                      {shift.active && <span className="active-dot" />}
                      <span>{shift.name}</span>
                    </div>
                    <span className={`shift-badge ${shift.active ? 'badge-active' : 'badge-closed'}`}>
                      {shift.active ? 'Activo' : 'Cerrado'}
                    </span>
                  </div>
                  <div className="shift-details">
                    <div className="shift-detail-row">
                      <span className="detail-label">Apertura:</span>
                      <span className="detail-value">
                        {formatDateTime(shift.start_time)} – {shift.open_username}
                      </span>
                    </div>
                    {shift.end_time && (
                      <div className="shift-detail-row">
                        <span className="detail-label">Cierre:</span>
                        <span className="detail-value">
                          {formatDateTime(shift.end_time)} – {shift.close_username}
                        </span>
                      </div>
                    )}
                    {shift.active && (
                      <div className="shift-detail-row">
                        <span className="detail-label">Duración:</span>
                        <span className="detail-value duration-badge">
                          {(() => {
                            const start = moment(shift.start_time);
                            const now = moment();
                            const duration = moment.duration(now.diff(start));
                            const hours = Math.floor(duration.asHours());
                            const minutes = Math.floor(duration.minutes());
                            return `${hours}h ${minutes}m`;
                          })()}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              ))
            ) : (
              <div className="no-shifts">
                <p>No hay información de turnos disponible</p>
              </div>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="panel-actions">
          <button
            className="end-session-btn"
            onClick={handleOpenEndSession}
          >
            Finalizar Sesión
          </button>
        </div>
      </div>
    );

    return (
      <>
        <div className="session-compact-container" ref={panelRef}>
          <button 
            className={`session-compact-button active-session-btn ${isExpanded ? 'expanded' : ''}`}
            onClick={toggleExpanded}
          >
            <HiOutlineClock className="compact-icon pulsing" />
            <span className="compact-text">{elapsedTime}</span>
            {isExpanded ? <HiOutlineChevronUp className="expand-icon" /> : <HiOutlineChevronDown className="expand-icon" />}
          </button>

          {/* Desktop: render panel inline */}
          {isExpanded && !isMobile && panelContent}
        </div>

        {/* Mobile: render panel as portal to body */}
        {isExpanded && isMobile && ReactDOM.createPortal(
          panelContent,
          document.body
        )}
      </>
    );
  };


  return (
    <div className="session-info-container">
      {!session && renderNoSession()}
      {session && !session.active && renderLastSession()}
      {session && session.active && renderActiveSession()}

      <StartSession
        show={showStartSession}
        handleClose={() => setShowStartSession(false)}
      />
      <EndSession
        session={session}
        show={showEndSession}
        handleClose={() => setShowEndSession(false)}
      />
      <NewShiftModal
        show={showNewShift}
        handleClose={handleCloseNewShift}
        localSessionId={session?.local_session_id || 0}
        localId={session?.local_id || 0}
        userId={session?.user_id || 0}
        onShiftCreated={handleShiftCreatedWrapper}
      />

      {/* Session Alert for prolonged sessions */}
      {session && session.active && (
        <SessionAlert
          sessionStart={new Date(session.session_start)}
          onEndSession={() => setShowEndSession(true)}
        />
      )}
    </div>
  );
};

export default SessionInfo;
